import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Layout from './components/Layout';
import Login from './components/Login';
import Signup from './components/Signup';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './components/pages/Home';
import Dashboard from './components/pages/Dashboard';
import GoalList from './components/pages/GoalList';
import GoalDetail from './components/pages/GoalDetail';
import GoalForm from './components/pages/GoalForm';
import GoalTargets from './components/pages/GoalTargets';
import DepartmentList from './components/pages/DepartmentList';
import DepartmentForm from './components/pages/DepartmentForm';
import TargetMatrix from './components/pages/TargetMatrix';
import TargetMatrixSelect from './components/pages/TargetMatrixSelect';
import InactiveGoals from './components/pages/InactiveGoals';
import CustomDashboard from './components/pages/CustomDashboard';
import PasswordReset from './components/PasswordReset';
import UserManagement from './components/pages/UserManagement';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Configurar axios
axios.defaults.baseURL = API_BASE_URL;

// Decodificar payload do JWT (sem verificar assinatura - só para UI)
function decodeToken(token) {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

function App() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const loadDashboardData = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get('/dashboard', {
        params: filters
      });
      
      if (response.data.success) {
        setDashboardData(response.data.data);
      } else {
        setError(response.data.error || 'Erro ao carregar dados');
      }
    } catch (err) {
      console.error('Error loading dashboard:', err);
      if (err.response?.status === 403) {
        setError('Você precisa estar autenticado. Por favor, faça login em http://127.0.0.1:8000/login/ primeiro.');
      } else if (err.response?.status === 401) {
        setError('Sessão expirada. Por favor, faça login novamente em http://127.0.0.1:8000/login/');
      } else {
        setError(err.response?.data?.error || 'Erro ao conectar com o servidor');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyToken = useCallback(async () => {
    try {
      const response = await axios.get('/auth/verify');
      if (response.data.success) {
        setIsAuthenticated(true);
        setCurrentUser(response.data.user);
        loadDashboardData();
      }
    } catch (err) {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setCurrentUser(null);
    }
  }, [loadDashboardData]);

  useEffect(() => {
    // Verificar se há token salvo
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      verifyToken();
    }
  }, [verifyToken]);

  const handleLogin = async (username, password) => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.post('/auth/login', { username, password });
      
      if (response.data.success) {
        const { token } = response.data;
        localStorage.setItem('token', token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setIsAuthenticated(true);
        const decoded = decodeToken(token);
        setCurrentUser(decoded);
        await loadDashboardData();
        return true; // Retornar true para indicar sucesso
      }
      return false;
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao fazer login');
      return false;
    } finally {
      setLoading(false);
    }
  };

  return (
    <Router>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : 
          <Login onLogin={handleLogin} loading={loading} error={error} />
        } />
        <Route path="/signup" element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : 
          <Signup />
        } />
        <Route path="/password-reset" element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : 
          <PasswordReset />
        } />
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Layout currentUser={currentUser}>
                <Dashboard 
                  data={dashboardData} 
                  onFilterChange={loadDashboardData}
                  loading={loading}
                  error={error}
                />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard/custom" 
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Layout currentUser={currentUser}>
                <CustomDashboard />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/goals" 
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Layout currentUser={currentUser}>
                <GoalList />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/goals/create" 
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Layout currentUser={currentUser}>
                <GoalForm />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/goals/:id" 
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Layout currentUser={currentUser}>
                <GoalDetail />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/goals/:id/edit" 
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Layout currentUser={currentUser}>
                <GoalForm />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/goals/:id/targets" 
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Layout currentUser={currentUser}>
                <GoalTargets />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/goals/inactive" 
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Layout currentUser={currentUser}>
                <InactiveGoals />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/departments" 
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Layout currentUser={currentUser}>
                <DepartmentList currentUser={currentUser} />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/departments/create" 
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Layout currentUser={currentUser}>
                <DepartmentForm currentUser={currentUser} />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/departments/:id/edit" 
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Layout currentUser={currentUser}>
                <DepartmentForm currentUser={currentUser} />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/matrix" 
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Layout currentUser={currentUser}>
                <TargetMatrixSelect />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/matrix/:period" 
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Layout currentUser={currentUser}>
                <TargetMatrix />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/users" 
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Layout currentUser={currentUser}>
                <UserManagement currentUser={currentUser} />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
